/**
 * Gets the CSP nonce value from the root div element.
 * This nonce is injected by the server-side middleware and should be used
 * when creating dynamic style elements to comply with Content Security Policy.
 * 
 * @returns The CSP nonce value, or null if not found
 */
export function getCspNonce(): string | null {
    const rootElement = document.getElementById('root');
    return rootElement?.getAttribute('data-csp-nonce') || null;
}

/**
 * Creates a style element with the CSP nonce attribute.
 * This ensures that dynamically created styles comply with Content Security Policy.
 * 
 * @param cssText - The CSS text to inject
 * @param id - Optional ID for the style element
 * @returns The created style element
 */
export function createStyleElementWithNonce(cssText: string, id?: string): HTMLStyleElement {
    const style = document.createElement('style');
    const nonce = getCspNonce();
    
    if (nonce) {
        style.setAttribute('nonce', nonce);
    }
    
    if (id) {
        style.id = id;
    }
    
    style.textContent = cssText;
    return style;
}

/**
 * Appends a style element with CSP nonce to the document head.
 * 
 * @param cssText - The CSS text to inject
 * @param id - Optional ID for the style element
 * @returns The created and appended style element
 */
export function appendStyleWithNonce(cssText: string, id?: string): HTMLStyleElement {
    const style = createStyleElementWithNonce(cssText, id);
    document.head.appendChild(style);
    return style;
}

/**
 * Removes a style element by ID from the document head.
 * 
 * @param id - The ID of the style element to remove
 */
export function removeStyleById(id: string): void {
    const style = document.getElementById(id);
    if (style && style.tagName === 'STYLE') {
        style.remove();
    }
}

/**
 * Adds nonce to a style element if it doesn't already have one.
 * 
 * @param styleElement - The style element to add nonce to
 */
function addNonceToStyleElement(styleElement: HTMLStyleElement): void {
    if (!styleElement.hasAttribute('nonce')) {
        const nonce = getCspNonce();
        if (nonce) {
            styleElement.setAttribute('nonce', nonce);
        }
    }
}

/**
 * Patches document.createElement to automatically add CSP nonce to style elements.
 * This ensures that third-party libraries that create style elements dynamically
 * will automatically comply with Content Security Policy.
 * 
 * This should be called once during app initialization.
 * The nonce is read dynamically each time a style element is created to ensure
 * it's available even if the DOM isn't fully loaded when the patch is applied.
 * 
 * @returns A function to restore the original createElement behavior
 */
export function patchCreateElementForCspNonce(): () => void {
    const originalCreateElement = document.createElement.bind(document);
    
    document.createElement = function<T extends keyof HTMLElementTagNameMap>(
        tagName: T,
        options?: ElementCreationOptions
    ): HTMLElementTagNameMap[T] {
        const element = originalCreateElement(tagName, options);
        
        // Automatically add nonce to style elements
        if (tagName.toLowerCase() === 'style' && element instanceof HTMLStyleElement) {
            addNonceToStyleElement(element);
        }
        
        return element;
    } as typeof document.createElement;
    
    // Return function to restore original behavior
    return () => {
        document.createElement = originalCreateElement;
    };
}

/**
 * Sets up a MutationObserver to automatically add CSP nonce to style elements
 * that are added to the DOM after the initial page load. This catches styles
 * injected by webpack, react-scripts, or other build tools.
 * 
 * This should be called once during app initialization.
 * 
 * @returns A function to disconnect the observer
 */
export function observeStyleElementsForCspNonce(): () => void {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as HTMLElement;
                    
                    // Check if the added node is a style element
                    if (element.tagName === 'STYLE' && element instanceof HTMLStyleElement) {
                        addNonceToStyleElement(element);
                    }
                    
                    // Also check for style elements within the added node
                    const styleElements = element.querySelectorAll?.('style');
                    if (styleElements) {
                        styleElements.forEach((style) => {
                            if (style instanceof HTMLStyleElement) {
                                addNonceToStyleElement(style);
                            }
                        });
                    }
                }
            });
        });
    });
    
    // Function to set up observation
    const setupObserver = () => {
        // Start observing the document head for added style elements
        if (document.head) {
            observer.observe(document.head, {
                childList: true,
                subtree: true
            });
        }
        
        // Also observe the document body in case styles are added there
        if (document.body) {
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
        
        // Also observe the document element itself as a fallback
        if (document.documentElement) {
            observer.observe(document.documentElement, {
                childList: true,
                subtree: true
            });
        }
        
        // Check existing style elements in the head and body
        const checkExistingStyles = () => {
            if (document.head) {
                const headStyles = document.head.querySelectorAll('style');
                headStyles.forEach((style) => {
                    if (style instanceof HTMLStyleElement) {
                        addNonceToStyleElement(style);
                    }
                });
            }
            
            if (document.body) {
                const bodyStyles = document.body.querySelectorAll('style');
                bodyStyles.forEach((style) => {
                    if (style instanceof HTMLStyleElement) {
                        addNonceToStyleElement(style);
                    }
                });
            }
        };
        
        // Check immediately
        checkExistingStyles();
        
        // Also check after a short delay to catch styles added during initial load
        setTimeout(checkExistingStyles, 100);
        setTimeout(checkExistingStyles, 500);
        setTimeout(checkExistingStyles, 1000);
    };
    
    // Set up observer immediately if DOM is ready, otherwise wait
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupObserver);
    } else {
        setupObserver();
    }
    
    // Return function to disconnect the observer
    return () => {
        observer.disconnect();
    };
}
